import {
  extractFileType,
  getFileContentType,
  GetUploadEndpoint,
  SetItemContentType,
  UploadFileToProvider,
} from '@ui/uploads/validations';
import { FILE_NUMBER_MAP, FILE_TYPES, THUMBNAIL_TYPES } from './content-utils';
import { ThumbnailStatus } from '@/types/module.types';
import { isObject } from 'underscore';
import { ConvertDocument, PostDocument } from '@/connection/content/documents';
import { Document } from '@/types/module.types';

export const UploadAndConvertDocument = async (
  file: File,
  onProgress?: (uploadedBytes: number) => void,
  headers?: Record<string, string>,
  filename?: string,
  thumbnail?: string
): Promise<Document | undefined> => {
  try {
    const fileToUpload = new File(
      [file],
      file.name.replace(/[`~!@#$%^&*|+=?;:'",<>{}[\]\\/]/gi, '').replace(/\s/g, '_')
    );
    const fileName = fileToUpload.name;

    let contentType = fileToUpload.type;
    if (!fileToUpload.type) {
      contentType = await getFileContentType(file);
    }

    const endpoint = await GetUploadEndpoint({ filename: file.name, contentType, headers });

    if (isObject(endpoint) && endpoint.url) {
      const splitKey = endpoint.fields.Key.split('/');
      const prefix = splitKey.slice(0, splitKey.length - 1).join('/');
      const finalUrl = await UploadFileToProvider(
        {
          url: endpoint.url,
          fields: endpoint.fields,
          cdnHost: endpoint?.cdnHost,
        },
        file,
        onProgress
      );
      SetItemContentType({ filename: endpoint.fields.Key, contentType: file.type }).catch(console.error);

      const fileExt = extractFileType(file.name);
      const location = finalUrl;

      const isImage = FILE_NUMBER_MAP[fileExt] === FILE_TYPES.IMAGES;
      const isThumbnailType = THUMBNAIL_TYPES.has(fileExt);

      const doc = await PostDocument({
        original_name: file.name,
        location: location,
        thumbnail: (thumbnail ?? isImage) ? location : null,
        thumbnail_status: thumbnail
          ? ThumbnailStatus.done
          : isThumbnailType
            ? ThumbnailStatus.generating
            : ThumbnailStatus.never,
        filesize: file.size,
        display_name: { base: filename ?? file.name, changed: 'true' },
        generated_thumbnail: null,
      });

      if (isThumbnailType) {
        await ConvertDocument({
          document: doc.document,
          ext: fileExt,
          prefix: prefix,
          filename: fileName,
        });
      }
      return doc;
    }
  } catch (e) {
    console.error(e);
  }
};

export const getVideoDuration = (file: File): Promise<number> =>
  new Promise<number>((resolve, reject) => {
    try {
      // tested with large multi-GB video, resolves in under a second, just giving a very large buffer
      // in case file is corrupted. If this does not time out and the file is not playable, promise will never resolve
      // and the upload will stall forever without any error
      const timeout = setTimeout(() => {
        reject('Unable to get thumbnail in reasonable amount of time');
      }, 10000);
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.onloadedmetadata = () => {
        const duration = video.duration || 0;
        window.URL.revokeObjectURL(video.src);
        resolve(Math.trunc(duration));
        clearTimeout(timeout);
      };
      video.src = window.URL.createObjectURL(file);
    } catch (e) {
      reject(e);
    }
  });

// export const putVideoThumbnail = async (videoUrl: string, token: string, user: BLAdmin): Promise<string | null>  =>{
// 	try {
// 		const thumbnailData = await getVideoThumbnail(videoUrl);
// 		const thumbFile = new File([thumbnailData], 'thumbnail.jpg', {'type': 'image/jpeg' });
// 		return await UploadFile(user, token, thumbFile);
// 	} catch (e) {
// 		console.error(e);
// 		return null;
// 	}
// }

export const getVideoThumbnail = (file: File | string): Promise<File | Blob> => {
  return new Promise((resolve, reject) => {
    try {
      const url = file instanceof File ? URL.createObjectURL(file) : file;
      const videoEl = document.createElement('video');

      if (typeof url === 'string') {
        videoEl.crossOrigin = 'anonymous';
      }

      videoEl.src = url;
      videoEl.muted = true;

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      videoEl.addEventListener('playing', () => {
        videoEl.currentTime = videoEl.duration / 1.5;
      });

      videoEl.addEventListener('seeked', () => {
        canvas.width = videoEl.videoWidth;
        canvas.height = videoEl.videoHeight;

        ctx?.drawImage(videoEl, 0, 0, videoEl.videoWidth, videoEl.videoHeight);
        const frame = canvas.toDataURL('image/jpeg', 0.8);

        resolve(dataURItoBlob(frame));
        videoEl.pause();
        videoEl.remove();
        canvas.remove();
      });

      videoEl.play();
    } catch (e) {
      reject(e);
    }
  });
};

const dataURItoBlob = (dataURI: string) => {
  // convert base64/URLEncoded data component to raw binary data held in a string
  let byteString;
  if (dataURI.split(',')[0].indexOf('base64') >= 0) byteString = atob(dataURI.split(',')[1]);
  else byteString = unescape(dataURI.split(',')[1]);

  // separate out the mime component
  const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];

  // write the bytes of the string to a typed array
  const ia = new Uint8Array(byteString.length);
  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }

  return new Blob([ia], { type: mimeString });
};
